import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  XCircle,
  AlertTriangle,
  Users,
  TrendingDown,
  RefreshCw,
  Calendar,
  Filter,
  User,
  Car,
  AlertCircle,
  IndianRupee,
  Clock,
  CheckCircle,
} from "lucide-react";

export const CancelledRidesAnalytics = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [cities, setCities] = useState([]);
  
  // Filters
  const [cityFilter, setCityFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    if (user?.id) {
      fetchCities();
      fetchAnalytics();
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchAnalytics();
    }
  }, [cityFilter, dateFrom, dateTo]);

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${API}/cities?admin_id=${user.id}`);
      // Sort cities alphabetically
      const sortedCities = (res.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCities(sortedCities);
    } catch (err) {
      console.error("Failed to load cities", err);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cityFilter && cityFilter !== "all") params.append("city_id", cityFilter);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      
      const res = await axios.get(`${API}/admin/${user.id}/cancelled-rides-analytics?${params.toString()}`);
      setAnalytics(res.data);
    } catch (err) {
      toast.error("Failed to load cancellation analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cancelled-rides-analytics">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <XCircle className="w-6 h-6 text-red-500" />
            Cancelled Rides Analytics
          </h1>
          <p className="text-sm text-muted-foreground">
            Analyze cancellation patterns and frequent cancellers
          </p>
        </div>
        <Button variant="outline" onClick={fetchAnalytics}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-card border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium text-sm">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-sm">City</Label>
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger data-testid="city-filter">
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map((city) => (
                  <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">From Date</Label>
            <div className="relative">
              <Calendar className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="pl-8"
                data-testid="date-from"
              />
            </div>
          </div>
          <div>
            <Label className="text-sm">To Date</Label>
            <div className="relative">
              <Calendar className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="pl-8"
                data-testid="date-to"
              />
            </div>
          </div>
          <div className="flex items-end">
            <Button onClick={fetchAnalytics} className="w-full">Apply Filters</Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <span className="text-sm text-red-600">Total Cancelled</span>
          </div>
          <p className="text-3xl font-bold text-red-700">{analytics?.total_cancelled || 0}</p>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-5 h-5 text-yellow-600" />
            <span className="text-sm text-yellow-600">By Customer</span>
          </div>
          <p className="text-3xl font-bold text-yellow-700">{analytics?.cancellation_by_who?.customer || 0}</p>
        </div>
        
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Car className="w-5 h-5 text-orange-600" />
            <span className="text-sm text-orange-600">By Driver</span>
          </div>
          <p className="text-3xl font-bold text-orange-700">{analytics?.cancellation_by_who?.driver || 0}</p>
        </div>
        
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-purple-600" />
            <span className="text-sm text-purple-600">By Admin</span>
          </div>
          <p className="text-3xl font-bold text-purple-700">{analytics?.cancellation_by_who?.admin || 0}</p>
        </div>
        
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-gray-600" />
            <span className="text-sm text-gray-600">System</span>
          </div>
          <p className="text-3xl font-bold text-gray-700">{analytics?.cancellation_by_who?.system || 0}</p>
        </div>
      </div>

      {/* Top Cancellation Reasons */}
      <div className="bg-card border rounded-lg p-4">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <TrendingDown className="w-5 h-5 text-red-500" />
          Top Cancellation Reasons
        </h3>
        {analytics?.top_reasons?.length > 0 ? (
          <div className="space-y-3">
            {analytics.top_reasons.map((reason, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    idx === 0 ? "bg-red-100 text-red-700" :
                    idx === 1 ? "bg-orange-100 text-orange-700" :
                    idx === 2 ? "bg-yellow-100 text-yellow-700" :
                    "bg-gray-100 text-gray-700"
                  }`}>
                    {idx + 1}
                  </span>
                  <span className="font-medium">{reason.reason}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-red-500 h-2.5 rounded-full" 
                      style={{ width: `${Math.min(100, (reason.count / (analytics.total_cancelled || 1)) * 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium w-12 text-right">{reason.count}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-4">No cancellation data available</p>
        )}
      </div>

      {/* Frequent Cancellers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Frequent Customer Cancellers */}
        <div className="bg-card border rounded-lg p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-yellow-500" />
            Frequent Customer Cancellers
          </h3>
          {analytics?.frequent_customer_cancellers?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Cancellations</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analytics.frequent_customer_cancellers.map((customer, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{customer.customer_name || "Unknown"}</p>
                        <p className="text-sm text-muted-foreground">{customer.customer_phone}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        customer.count >= 5 ? "bg-red-100 text-red-700" :
                        customer.count >= 3 ? "bg-orange-100 text-orange-700" :
                        "bg-yellow-100 text-yellow-700"
                      }`}>
                        {customer.count}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground text-center py-4">No frequent cancellers found</p>
          )}
        </div>

        {/* Frequent Driver Cancellations */}
        <div className="bg-card border rounded-lg p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Car className="w-5 h-5 text-orange-500" />
            Drivers with Most Cancellations
          </h3>
          {analytics?.frequent_driver_cancellers?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead className="text-right">Cancellations</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analytics.frequent_driver_cancellers.map((driver, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <p className="font-medium">{driver.driver_name || "Unknown"}</p>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        driver.count >= 5 ? "bg-red-100 text-red-700" :
                        driver.count >= 3 ? "bg-orange-100 text-orange-700" :
                        "bg-yellow-100 text-yellow-700"
                      }`}>
                        {driver.count}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground text-center py-4">No driver cancellations found</p>
          )}
        </div>
      </div>

      {/* Customer Cancellations After Driver Accepted - NEW SECTION */}
      {analytics?.customer_cancellations_after_acceptance && analytics.customer_cancellations_after_acceptance.total > 0 && (
        <div className="bg-card border border-amber-200 rounded-lg p-4" data-testid="cancellations-after-acceptance">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Customer Cancellations After Driver Accepted
              <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-sm">
                {analytics.customer_cancellations_after_acceptance.total}
              </span>
            </h3>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4">
            These rides were cancelled by the customer <strong>after</strong> a driver had already accepted the ride.
            This impacts driver earnings and satisfaction.
          </p>

          {/* Most Affected Drivers */}
          {analytics.customer_cancellations_after_acceptance.most_affected_drivers?.length > 0 && (
            <div className="bg-amber-50 rounded-lg p-4 mb-4" data-testid="most-affected-drivers">
              <h4 className="font-medium text-amber-800 mb-3 flex items-center gap-2">
                <Car className="w-4 h-4" />
                Most Affected Drivers
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {analytics.customer_cancellations_after_acceptance.most_affected_drivers.slice(0, 8).map((driver, idx) => (
                  <div key={driver.driver_id || idx} className="bg-white rounded-lg p-3 border border-amber-200">
                    <p className="font-medium text-sm truncate">{driver.driver_name || "Unknown Driver"}</p>
                    <div className="flex items-center justify-between mt-2 text-xs">
                      <span className="text-amber-600 font-medium">{driver.count} lost rides</span>
                      <span className="text-muted-foreground flex items-center gap-1">
                        <IndianRupee className="w-3 h-3" />
                        {Math.round(driver.total_lost_fare || 0)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Cancellations Table */}
          {analytics.customer_cancellations_after_acceptance.recent_rides?.length > 0 && (
            <div data-testid="recent-cancellations-after-acceptance">
              <h4 className="font-medium text-amber-800 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Recent Cancelled Rides (Driver Had Accepted)
              </h4>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Driver</TableHead>
                      <TableHead>Accepted At</TableHead>
                      <TableHead>Cancelled At</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-right">Est. Fare</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics.customer_cancellations_after_acceptance.recent_rides.slice(0, 20).map((ride, idx) => (
                      <TableRow key={ride.ride_id || idx}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{ride.customer_name || "Unknown"}</p>
                            <p className="text-xs text-muted-foreground">{ride.customer_phone}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium text-sm text-amber-700">{ride.driver_name || "Unknown"}</p>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {ride.accepted_at ? new Date(ride.accepted_at).toLocaleString('en-IN', {
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          }) : '-'}
                        </TableCell>
                        <TableCell className="text-xs text-red-600">
                          {ride.cancelled_at ? new Date(ride.cancelled_at).toLocaleString('en-IN', {
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          }) : '-'}
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          <span className="text-xs text-muted-foreground truncate block">
                            {ride.cancellation_reason || "No reason provided"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ₹{Math.round(ride.estimated_fare || 0)}
                        </TableCell>
                        <TableCell>
                          {ride.was_rebooked ? (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex items-center gap-1 w-fit">
                              <CheckCircle className="w-3 h-3" /> Rebooked
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                              Not Rebooked
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CancelledRidesAnalytics;
